import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Divider,
  useMediaQuery,
  Button,
  Modal,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { buildReminderMessage } from "../utils/reminder";
import { useOrganization } from "../utils/OrganizationContext";
import ErrorModal from "../utils/ErrorModal";

/* ---------- Direction C tokens (styling only) ---------- */

const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const LINE_SOFT = "#F1F4F8";
const PRIMARY = "#0B5C8A";

const panelSx = {
  background: "#FFFFFF",
  border: `1px solid ${LINE}`,
  borderRadius: "14px",
  overflow: "hidden",
  mb: 2,
  fontFamily: FONT,
};
const panelHeadSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  px: 2,
  py: 1.5,
  borderBottom: "1px solid #EDF1F5",
};
const panelTitleSx = {
  fontFamily: FONT,
  fontSize: 15,
  fontWeight: 700,
  letterSpacing: "-0.01em",
  color: INK,
};
const countSx = {
  fontFamily: MONO,
  fontSize: 11.5,
  fontWeight: 600,
  color: MUTED,
};
const emptySx = {
  fontFamily: FONT,
  fontSize: 13.5,
  color: MUTED,
  mb: 1.25,
};
const emptyWrapSx = { px: 2, py: 2.25 };
const primaryBtnSx = {
  height: 36,
  px: 2,
  borderRadius: "9px !important",
  background: `${PRIMARY} !important`,
  color: "#FFFFFF !important",
  border: "0 !important",
  fontFamily: FONT,
  fontSize: 13.5,
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { background: "#084767 !important" },
  "&.Mui-disabled": { opacity: 0.6 },
};
const outlineBtnSx = {
  height: 36,
  px: 2,
  borderRadius: "9px !important",
  background: "#FFFFFF !important",
  color: `${INK} !important`,
  border: "1px solid #D5DEE7 !important",
  fontFamily: FONT,
  fontSize: 13.5,
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { background: "#F4F7FA !important" },
};
const pillSx = {
  display: "inline-block",
  px: 1.125,
  py: 0.25,
  borderRadius: "999px",
  fontFamily: FONT,
  fontSize: 11.5,
  fontWeight: 600,
  lineHeight: 1.5,
  whiteSpace: "nowrap",
};
const projectPillSx = {
  ...pillSx,
  background: "#F1F4F8",
  border: `1px solid ${LINE}`,
  color: INK_2,
};

// Colour a status pill from its wording — visual only, statuses are unchanged.
const statusTone = (status) => {
  const s = (status || "").toLowerCase();
  if (!s || s.includes("new")) return { bg: "#EAF2FB", edge: "#C9DEF3", ink: "#1B4F82" };
  if (s.includes("lost") || s.includes("not interested") || s.includes("non-responsive") || s.includes("mismatch"))
    return { bg: "#FBEDEB", edge: "#F2CFCA", ink: "#A6342A" };
  if (s.includes("visit")) return { bg: "#FDF4E1", edge: "#F3E0B4", ink: "#8A5A08" };
  if (s.includes("negotiation") || s.includes("proposal") || s.includes("offer") || s.includes("terms"))
    return { bg: "#FBEAF3", edge: "#F2CDE1", ink: "#9A2F69" };
  if (s.includes("token") || s.includes("booking") || s.includes("agreement") || s.includes("registered") || s.includes("possession"))
    return { bg: "#E9F7EE", edge: "#C4E8D2", ink: "#1B6B41" };
  if (s.includes("interested") || s.includes("requirement") || s.includes("shortlist"))
    return { bg: "#E6F6F5", edge: "#BFE6E3", ink: "#11645E" };
  return { bg: "#EFEDFB", edge: "#D3D0F2", ink: "#4A3FBF" };
};

/* ---------- Utils ---------- */

const isToday = (date) => {
  if (!date) return false;
  const today = new Date();
  const d = new Date(date);
  return (
    d.getDate() === today.getDate() &&
    d.getMonth() === today.getMonth() &&
    d.getFullYear() === today.getFullYear()
  );
};

// Roles allowed to see/manage the "Projects" priority-assignment panel.
// Adjust "PROJECT_HEAD" to match whatever your org context actually returns.
const PROJECT_PANEL_ROLES = ["ADMIN", "PROJECT_HEAD"];

/* ---------- Main Component ---------- */

const TodayTasks = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [tab, setTab] = useState(0);
  const [leads, setLeads] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectPendingLeadsMap, setProjectPendingLeadsMap] = useState({});
  const [projectLeads, setProjectLeads] = useState([]);
  const [priorityModalOpen, setPriorityModalOpen] = useState(false);
  const [currentLeadIndex, setCurrentLeadIndex] = useState(0);
  const [selectedPriority, setSelectedPriority] = useState("");
  const [saving, setSaving] = useState(false);
  const [reminders, setReminders] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMsg, setModalMsg] = useState("");
  const showModal = (msg) => {
    setModalMsg(msg);
    setModalOpen(true);
  };
  const user = JSON.parse(localStorage.getItem("user"));
  const location = useLocation();
  const navigate = useNavigate();
  const orgId = JSON.parse(localStorage.getItem("org"));
  const { role } = useOrganization();

  const isProjectManager = PROJECT_PANEL_ROLES.includes(role);

  useEffect(() => {
    const fetchData = async () => {
      const [leadsRes, remindersRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/lead/today?orgId=${orgId}`, {
          headers: { Authorization: `Bearer ${user.accessToken}` },
        }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/reminder/all?orgId=${orgId}`, {
          headers: { Authorization: `Bearer ${user.accessToken}` },
        }),
      ]);
      setLeads(leadsRes.data || []);
      setReminders(remindersRes.data || []);
    };

    fetchData();
  }, [orgId]);

  // Fetch projects, then filter down to only those that still have leads
  // with an unset priority. Feature is restricted to admin/project head.
  useEffect(() => {
    if (!isProjectManager) {
      setProjects([]);
      setProjectPendingLeadsMap({});
      setProjectsLoading(false);
      return;
    }

    const fetchProjectsWithPendingPriority = async () => {
      setProjectsLoading(true);
      try {
        const url =
          role === "ADMIN"
            ? `${process.env.REACT_APP_BACKEND_URL}/project/all?orgId=${orgId}`
            : `${process.env.REACT_APP_BACKEND_URL}/project/team/all?orgId=${orgId}`;

        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${user.accessToken}` },
        });

        const allProjects = res.data || [];

        // Check each project for leads still needing a priority assigned.
        const results = await Promise.all(
          allProjects.map(async (project) => {
            try {
              const leadsRes = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/lead/priority?projectId=${project.projectId}&orgId=${orgId}`,
                { headers: { Authorization: `Bearer ${user.accessToken}` } },
              );
              return { project, pendingLeads: leadsRes.data || [] };
            } catch (err) {
              console.log(err);
              return { project, pendingLeads: [] };
            }
          }),
        );

        // Only keep projects that actually have pending-priority leads.
        const withPending = results.filter((r) => r.pendingLeads.length > 0);

        setProjects(withPending.map((r) => r.project));
        setProjectPendingLeadsMap(
          withPending.reduce((acc, r) => {
            acc[r.project.projectId] = r.pendingLeads;
            return acc;
          }, {}),
        );
      } catch (error) {
        console.log(error);
      } finally {
        setProjectsLoading(false);
      }
    };

    fetchProjectsWithPendingPriority();
  }, [role, orgId, isProjectManager]);

  const openProjectPriority = async (project) => {
    try {
      // Reuse the leads already fetched while building the list, falling
      // back to a fresh fetch if the cache is somehow missing them.
      const cached = projectPendingLeadsMap[project.projectId];
      const pendingLeads =
        cached ??
        (
          await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/lead/priority?projectId=${project.projectId}&orgId=${orgId}`,
            { headers: { Authorization: `Bearer ${user.accessToken}` } },
          )
        ).data;

      setProjectLeads(pendingLeads || []);
      setCurrentLeadIndex(0);
      setSelectedPriority("");
      setPriorityModalOpen(true);
    } catch (err) {
      console.log(err);
    }
  };

  const currentLead = projectLeads[currentLeadIndex];

  const handleSavePriority = async () => {
    if (!selectedPriority) {
      showModal("Please select priority");
      return;
    }

    try {
      setSaving(true);

      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/lead?leadId=${currentLead.leadId}`,
        {
          orgId: orgId,
          priority: selectedPriority,
        },
        {
          headers: {
            Authorization: `Bearer ${user.accessToken}`,
          },
        },
      );

      setSelectedPriority("");

      if (currentLeadIndex < projectLeads.length - 1) {
        setCurrentLeadIndex((prev) => prev + 1);
      } else {
        setCurrentLeadIndex(projectLeads.length);

        // This project no longer has pending-priority leads — drop it from
        // the list without waiting for a full refetch.
        setProjects((prev) =>
          prev.filter((p) => p.projectId !== currentLead.projectId),
        );
      }
    } catch (error) {
      console.log(error);
      showModal(error?.response?.data?.Exception || "Failed to save priority");
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Derived “Today” Data ---------- */

  const todaysLeads = useMemo(
    () =>
      leads.filter(
        (l) =>
          l.status === null || l.status === "NEW" || l.status === "New Lead" || isToday(l.nextFollowUp),
      ),
    [leads],
  );
  const todaysReminders = useMemo(
    () => reminders.filter((r) => isToday(r.dateTime)),
    [reminders],
  );

  /* ---------- Summary strip (derived from the data already loaded above) ---------- */

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const dueTodayCount = leads.filter((l) => isToday(l.nextFollowUp)).length;
  const overdueCount = leads.filter(
    (l) => l.nextFollowUp && new Date(l.nextFollowUp) < startOfToday,
  ).length;
  const newLeadsCount = leads.filter(
    (l) => l.status === null || l.status === "NEW" || l.status === "New Lead",
  ).length;
  const pendingPriorityCount = Object.values(projectPendingLeadsMap).reduce(
    (sum, list) => sum + (list?.length || 0),
    0,
  );

  const summaryStats = [
    { label: "Due today", value: dueTodayCount, note: "follow-ups scheduled", tint: "#FDF4E1", edge: "#F3E0B4", ink: "#8A5A08" },
    { label: "Overdue", value: overdueCount, note: "past their follow-up", tint: "#FBEDEB", edge: "#F2CFCA", ink: "#A6342A" },
    { label: "New leads", value: newLeadsCount, note: "not yet actioned", tint: "#EAF2FB", edge: "#C9DEF3", ink: "#1B4F82" },
    isProjectManager
      ? { label: "Need priority", value: pendingPriorityCount, note: `across ${projects.length} project${projects.length === 1 ? "" : "s"}`, tint: "#EFEDFB", edge: "#D3D0F2", ink: "#4A3FBF" }
      : { label: "Reminders today", value: todaysReminders.length, note: "on your calendar", tint: "#E6F6F2", edge: "#BFE6DC", ink: "#146B58" },
  ];

  const todayLabel = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  const summaryStrip = (
    <Box sx={{ fontFamily: FONT, mb: 2 }}>
      {/* Hero band — same as the design's Today header */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 2,
          p: { xs: "18px 16px", sm: "22px 24px" },
          mb: 2,
          borderRadius: "16px",
          background: "linear-gradient(135deg, #0B5C8A 0%, #0A3F63 100%)",
          color: "#FFFFFF",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.72)" }}>
            {todayLabel}
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: { xs: 24, sm: 30 }, fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.15, color: "#FFFFFF", mt: 0.5 }}>
            {greeting}
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 500, color: "rgba(255,255,255,0.82)", mt: 0.5 }}>
            {dueTodayCount} follow-up{dueTodayCount === 1 ? "" : "s"} due
            {overdueCount ? ` · ${overdueCount} overdue` : ""} · {todaysReminders.length} reminder{todaysReminders.length === 1 ? "" : "s"} today
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            onClick={() => navigate("/dashboard/add-lead")}
            sx={{ height: 38, px: 2, borderRadius: "10px", border: "1px solid rgba(255,255,255,0.35)", background: "rgba(255,255,255,0.08)", color: "#FFFFFF", fontFamily: FONT, fontSize: 13.5, fontWeight: 600, textTransform: "none", "&:hover": { background: "rgba(255,255,255,0.16)" } }}
          >
            Add lead
          </Button>
          <Button
            onClick={() => navigate(`${location.pathname}/leads`)}
            sx={{ height: 38, px: 2, borderRadius: "10px", background: "#FFFFFF", color: "#0B5C8A", fontFamily: FONT, fontSize: 13.5, fontWeight: 700, textTransform: "none", "&:hover": { background: "#EAF2FB" } }}
          >
            Open all leads
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
          gap: 1.5,
        }}
      >
        {summaryStats.map((s) => (
          <Box
            key={s.label}
            sx={{ p: "14px 16px", background: s.tint, border: `1px solid ${s.edge}`, borderRadius: "14px", minWidth: 0 }}
          >
            <Typography sx={{ fontFamily: FONT, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: s.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {s.label}
            </Typography>
            <Typography sx={{ fontFamily: MONO, fontSize: { xs: 26, sm: 32 }, fontWeight: 600, letterSpacing: "-0.04em", lineHeight: 1.1, color: s.ink, mt: 0.5 }}>
              {s.value}
            </Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: "#4A5A6E", mt: 0.25, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {s.note}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );

  /* ---------- Shared Priority Modal (used by both desktop & mobile) ---------- */

  const priorityBtnSx = (bg, edge, ink) => ({
    "&.Mui-selected": {
      backgroundColor: `${bg} !important`,
      borderColor: `${edge} !important`,
      color: `${ink} !important`,
    },
    "&.Mui-selected:hover": {
      backgroundColor: `${bg} !important`,
    },
  });

  const infoLabelSx = {
    display: "inline-block",
    width: 110,
    fontSize: 12,
    fontWeight: 600,
    color: MUTED,
  };
  const infoRowSx = {
    fontFamily: FONT,
    fontSize: 13.5,
    color: INK,
    py: 0.875,
    borderBottom: `1px solid ${LINE_SOFT}`,
  };

  const priorityModal = (
    <Modal
      open={priorityModalOpen}
      onClose={() => setPriorityModalOpen(false)}
      slotProps={{ backdrop: { sx: { background: "rgba(15,27,42,0.46)" } } }}
    >
      <Box
        sx={{
          width: 500,
          maxWidth: "95%",
          bgcolor: "#fff",
          p: 3,
          borderRadius: "18px",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          boxShadow: "0 32px 64px -20px rgba(15,27,42,0.5)",
          fontFamily: FONT,
          color: INK,
          outline: "none",
        }}
      >
        {projectLeads.length === 0 ? (
          <>
            <Typography variant="h6" sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 800, letterSpacing: "-0.02em" }}>
              No leads require priority assignment
            </Typography>

            <Button
              fullWidth
              sx={{ mt: 2.5, ...primaryBtnSx, height: 40 }}
              className="btn-contained"
              onClick={() => setPriorityModalOpen(false)}
            >
              Close
            </Button>
          </>
        ) : currentLeadIndex >= projectLeads.length ? (
          <>
            <Typography
              variant="h5"
              textAlign="center"
              fontWeight={700}
              mb={1}
              sx={{ fontFamily: FONT, fontSize: 22, fontWeight: 800, letterSpacing: "-0.025em" }}
            >
              🎉 All Leads Processed
            </Typography>

            <Typography textAlign="center" sx={{ fontFamily: FONT, fontSize: 14, color: MUTED }}>
              All priority assignments are completed.
            </Typography>

            <Button
              fullWidth
              sx={{ mt: 3, ...primaryBtnSx, height: 40 }}
              className="btn-contained"
              onClick={() => setPriorityModalOpen(false)}
            >
              Close
            </Button>
          </>
        ) : (
          <>
            <Typography
              variant="h6"
              sx={{
                fontFamily: MONO,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.11em",
                textTransform: "uppercase",
                color: PRIMARY,
              }}
            >
              Lead {currentLeadIndex + 1} of {projectLeads.length}
            </Typography>

            {/* progress through the project's pending leads */}
            <Box sx={{ mt: 1, height: 5, borderRadius: 3, background: "#EDF1F5", overflow: "hidden" }}>
              <Box
                sx={{
                  height: 5,
                  borderRadius: 3,
                  background: PRIMARY,
                  width: `${((currentLeadIndex + 1) / projectLeads.length) * 100}%`,
                  transition: "width .25s",
                }}
              />
            </Box>

            <Divider sx={{ my: 2, borderColor: "#EDF1F5" }} />

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                marginBottom: 6,
              }}
            >
              <Typography fontWeight={700} sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 800, letterSpacing: "-0.02em", minWidth: 0 }}>
                Lead Name: {currentLead.name}
              </Typography>
              <Typography variant="caption" sx={{ ...projectPillSx, flexShrink: 0 }}>
                {currentLead.projectName || "No Project"}
              </Typography>
            </div>

            <Typography color="text.secondary" sx={infoRowSx}>
              <span style={infoLabelSx}>Phone:</span>{" "}
              <span style={{ fontFamily: MONO, fontSize: 13 }}>
                {currentLead?.countryCode
                  ? currentLead.countryCode.includes("+")
                    ? currentLead.countryCode
                    : `+${currentLead.countryCode}`
                  : ""}
                {currentLead.phone}
              </span>
            </Typography>

            <Typography color="text.secondary" sx={infoRowSx}>
              <span style={infoLabelSx}>Email:</span> {currentLead.email || "-"}
            </Typography>

            <Typography color="text.secondary" sx={{ ...infoRowSx, borderBottom: 0 }}>
              <span style={infoLabelSx}>Latest Remark:</span>{" "}
              {currentLead.followUpHistories?.[0]?.remark || "-"}
            </Typography>

            <label
              style={{
                display: "block",
                marginTop: 16,
                fontFamily: FONT,
                fontSize: 12,
                fontWeight: 600,
                color: INK_2,
              }}
            >
              Priority
            </label>
            <ToggleButtonGroup
              value={selectedPriority}
              exclusive
              fullWidth
              onChange={(_, value) => {
                if (value) setSelectedPriority(value);
              }}
              sx={{
                mt: 1,
                gap: 1,
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  fontFamily: FONT,
                  fontSize: 13.5,
                  fontWeight: 700,
                  height: 40,
                  color: INK_2,
                  background: "#FFFFFF",
                  borderRadius: "10px !important",
                  border: "1px solid #D5DEE7 !important",
                  mx: 0,
                  "&:hover": { background: "#F4F7FA" },
                },
              }}
            >
              <ToggleButton value="LOW" sx={priorityBtnSx("#E9F7EE", "#6CC592", "#1B6B41")}>
                Low
              </ToggleButton>

              <ToggleButton value="MEDIUM" sx={priorityBtnSx("#FDF4E1", "#E8B04A", "#8A5A08")}>
                Medium
              </ToggleButton>

              <ToggleButton value="HIGH" sx={priorityBtnSx("#FBEDEB", "#E38A7F", "#A6342A")}>
                High
              </ToggleButton>
            </ToggleButtonGroup>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 10,
              }}
            >
              <Button
                className="btn-outlined"
                fullWidth
                sx={{ mt: 3, ...outlineBtnSx, height: 40 }}
                onClick={() =>
                  navigate(`/dashboard/lead/${currentLead.leadId}`)
                }
                target="_blank"
              >
                More Info
              </Button>
              <Button
                className="btn-contained"
                fullWidth
                sx={{ mt: 3, ...primaryBtnSx, height: 40 }}
                onClick={handleSavePriority}
                disabled={saving}
              >
                {saving
                  ? "Saving..."
                  : currentLeadIndex === projectLeads.length - 1
                    ? "Save & Finish"
                    : "Save & Next"}
              </Button>
            </div>
          </>
        )}
      </Box>
    </Modal>
  );

  const alertModal = (
    <ErrorModal
      open={modalOpen}
      handleClose={() => setModalOpen(false)}
      msg={modalMsg}
      type="error"
    />
  );

  /* ---------- Desktop View ---------- */
  if (!isMobile) {
    return (
      <>
      {summaryStrip}
      <Grid container spacing={2} mt={0} sx={{ fontFamily: FONT }}>
        <Grid size={{ xs: 7 }}>
          <Box sx={panelSx}>
            <Box sx={panelHeadSx}>
              <Typography variant="h6" sx={panelTitleSx}>Action Leads</Typography>
              <Box
                component="button"
                onClick={() => navigate(`${location.pathname}/leads`)}
                sx={{ border: 0, background: "transparent", p: 0, cursor: "pointer", fontFamily: FONT, fontSize: 13, fontWeight: 600, color: PRIMARY }}
              >
                Open all leads · <span style={{ fontFamily: MONO }}>{todaysLeads.length}</span>
              </Box>
            </Box>
            <Divider sx={{ display: "none" }} />
            {todaysLeads.length ? (
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {todaysLeads.map((lead, i) => (
                  <LeadCard key={lead.leadId} lead={lead} role={role} index={i} />
                ))}
              </Box>
            ) : (
              <Box sx={emptyWrapSx}>
                <Typography color="text.secondary" sx={emptySx}>
                  No leads scheduled for today
                </Typography>
                <Button
                  className="btn-contained"
                  sx={primaryBtnSx}
                  onClick={() => navigate(`${location.pathname}/leads`)}
                >
                  View All Leads
                </Button>
              </Box>
            )}
          </Box>
        </Grid>

        <Grid size={{ xs: 5 }}>
          <Box sx={panelSx}>
            <Box sx={panelHeadSx}>
              <Typography variant="h6" sx={panelTitleSx}>Reminders</Typography>
              <Box component="span" sx={countSx}>{todaysReminders.length}</Box>
            </Box>
            <Divider sx={{ display: "none" }} />
            <Box sx={{ maxHeight: "300px", overflowY: "auto" }}>
              {todaysReminders.length ? (
                todaysReminders.map((rem) => (
                  <ReminderCard key={rem.reminderId} reminder={rem} />
                ))
              ) : (
                <Box sx={emptyWrapSx}>
                  <Typography color="text.secondary" sx={emptySx}>
                    No reminders for today
                  </Typography>
                  <Button
                    className="btn-contained"
                    sx={primaryBtnSx}
                    onClick={() => navigate(`${location.pathname}/reminder`)}
                  >
                    View All Reminders
                  </Button>
                </Box>
              )}
            </Box>
          </Box>

          {isProjectManager && (
            <Box sx={panelSx}>
              <Box sx={panelHeadSx}>
                <Typography variant="h6" sx={panelTitleSx}>Needs a decision</Typography>
                <Box component="span" sx={countSx}>
                  {projectsLoading ? "…" : projects.length}
                </Box>
              </Box>
              <Divider sx={{ display: "none" }} />
              {projectsLoading ? (
                <Box sx={emptyWrapSx}>
                  <Typography color="text.secondary" sx={{ ...emptySx, mb: 0 }}>
                    Loading projects…
                  </Typography>
                </Box>
              ) : projects.length ? (
                projects.map((project) => (
                  <ProjectCard
                    key={project.projectId}
                    project={project}
                    pending={(projectPendingLeadsMap[project.projectId] || []).length}
                    onClick={() => openProjectPriority(project)}
                  />
                ))
              ) : (
                <Box sx={emptyWrapSx}>
                  <Typography color="text.secondary" sx={{ ...emptySx, mb: 0 }}>
                    No projects with pending priority assignments
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Grid>

        {isProjectManager && priorityModal}
      </Grid>
      {alertModal}
      </>
    );
  }

  /* ---------- Mobile View ---------- */
  const tabItems = [
    { key: "leads", label: `Leads (${todaysLeads.length})` },
    { key: "reminders", label: `Reminders (${todaysReminders.length})` },
    ...(isProjectManager
      ? [
          {
            key: "projects",
            label: projectsLoading ? "Projects" : `Projects (${projects.length})`,
          },
        ]
      : []),
  ];
  const activeKey = tabItems[tab]?.key;

  return (
    <Box sx={{ fontFamily: FONT }}>
      {summaryStrip}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          minHeight: 42,
          p: 0.5,
          background: "#FFFFFF",
          border: `1px solid ${LINE}`,
          borderRadius: "12px",
          "& .MuiTabs-indicator": { display: "none" },
          "& .MuiTab-root": {
            minHeight: 34,
            px: 1.75,
            borderRadius: "9px",
            textTransform: "none",
            fontFamily: FONT,
            fontSize: 13.5,
            fontWeight: 600,
            color: INK_2,
          },
          "& .MuiTab-root.Mui-selected": {
            background: "#0F1B2A",
            color: "#FFFFFF",
            fontWeight: 700,
          },
        }}
      >
        {tabItems.map((t) => (
          <Tab key={t.key} label={t.label} />
        ))}
      </Tabs>

      <Box mt={2} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {activeKey === "leads" &&
          (todaysLeads.length ? (
            <Box sx={{ background: "#FFFFFF", border: `1px solid ${LINE}`, borderRadius: "14px", overflow: "hidden" }}>
            {todaysLeads.map((lead, i) => (
              <LeadCard key={lead.leadId} lead={lead} role={role} index={i} />
            ))}
            </Box>
          ) : (
            <Box sx={{ ...panelSx, ...emptyWrapSx }}>
              <Typography color="text.secondary" sx={emptySx}>
                No leads scheduled for today
              </Typography>
              <Button
                className="btn-contained"
                sx={{ ...primaryBtnSx, height: 44, width: "100%" }}
                onClick={() => navigate(`${location.pathname}/leads`)}
              >
                View All Leads
              </Button>
            </Box>
          ))}

        {activeKey === "reminders" &&
          (todaysReminders.length ? (
            <Box sx={panelSx}>
              {todaysReminders.map((rem) => (
                <ReminderCard key={rem.reminderId} reminder={rem} />
              ))}
            </Box>
          ) : (
            <Box sx={{ ...panelSx, ...emptyWrapSx }}>
              <Typography color="text.secondary" sx={emptySx}>
                No reminders for today
              </Typography>
              <Button
                className="btn-contained"
                sx={{ ...primaryBtnSx, height: 44, width: "100%" }}
                onClick={() => navigate(`${location.pathname}/reminder`)}
              >
                View All Reminders
              </Button>
            </Box>
          ))}

        {activeKey === "projects" && isProjectManager && (
          <>
            {projectsLoading ? (
              <Box sx={{ ...panelSx, ...emptyWrapSx }}>
                <Typography color="text.secondary" sx={{ ...emptySx, mb: 0 }}>
                  Loading projects…
                </Typography>
              </Box>
            ) : projects.length ? (
              <Box sx={panelSx}>
                {projects.map((project) => (
                  <ProjectCard
                    key={project.projectId}
                    project={project}
                    pending={(projectPendingLeadsMap[project.projectId] || []).length}
                    onClick={() => openProjectPriority(project)}
                  />
                ))}
              </Box>
            ) : (
              <Box sx={{ ...panelSx, ...emptyWrapSx }}>
                <Typography color="text.secondary" sx={{ ...emptySx, mb: 0 }}>
                  No projects with pending priority assignments
                </Typography>
              </Box>
            )}

            {priorityModal}
          </>
        )}
      </Box>
      {alertModal}
    </Box>
  );
};

export default TodayTasks;

const LeadCard = ({ lead, role, index = 0 }) => {
  const tone = statusTone(lead.status);
  const code =
    lead.countryCode !== null && lead.countryCode !== undefined
      ? lead.countryCode.includes("+")
        ? lead.countryCode
        : `+${lead.countryCode}`
      : "";
  const digits = `${code}${lead.phone || ""}`.replace(/\D/g, "");

  // Due pill: Today (amber) / Overdue (red) / the date (grey)
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const due = lead.nextFollowUp ? new Date(lead.nextFollowUp) : null;
  const dueState = !due ? "none" : isToday(lead.nextFollowUp) ? "today" : due < start ? "overdue" : "later";
  const dueTone = {
    today: { bg: "#FDF4E1", edge: "#F3E0B4", ink: "#8A5A08", text: "Today" },
    overdue: { bg: "#FBEDEB", edge: "#F2CFCA", ink: "#A6342A", text: "Overdue" },
    later: { bg: "#F1F4F8", edge: LINE, ink: INK_2, text: due ? due.toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "" },
    none: null,
  }[dueState];

  const iconBtn = {
    flex: "0 0 auto",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 32,
    borderRadius: "9px",
    border: `1px solid ${LINE}`,
    background: "#FFFFFF",
    textDecoration: "none",
  };

  return (
    <Box
      onClick={() => window.open(`/dashboard/lead/${lead.leadId}`, "_blank", "noopener,noreferrer")}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.25,
        cursor: "pointer",
        borderBottom: `1px solid ${LINE_SOFT}`,
        background: dueState === "overdue" ? "#FFF8F7" : "#FFFFFF",
        "&:hover": { background: "#F7F9FC" },
      }}
    >
      <Box component="span" sx={{ flex: "0 0 22px", fontFamily: MONO, fontSize: 12, fontWeight: 600, color: "#8A97A8" }}>
        {String(index + 1).padStart(2, "0")}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontFamily: FONT, fontSize: 14, fontWeight: 700, letterSpacing: "-0.01em", color: INK, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {lead.name}
        </Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: MUTED, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          <span style={{ color: tone.ink, fontWeight: 600 }}>{lead.status || "New Lead"}</span>
          {" · "}
          {lead.projectName || "No Project"}
          {lead.followUpHistories?.[0]?.remark ? ` · ${lead.followUpHistories[0].remark}` : ""}
        </Typography>
      </Box>

      {dueTone && (
        <Box
          component="span"
          sx={{ ...pillSx, flexShrink: 0, fontFamily: MONO, background: dueTone.bg, border: `1px solid ${dueTone.edge}`, color: dueTone.ink }}
        >
          {dueTone.text}
        </Box>
      )}

      {digits && (
        <>
          <a
            href={`tel:+${digits}`}
            onClick={(e) => e.stopPropagation()}
            title="Call"
            style={{ ...iconBtn, color: "#1B4F82" }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.24 11.4 11.4 0 0 0 3.6.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1 11.4 11.4 0 0 0 .57 3.56 1 1 0 0 1-.25 1.02l-2.2 2.21z"/></svg>
          </a>
          <a
            href={`https://wa.me/${digits}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title="WhatsApp"
            style={{ ...iconBtn, color: "#1B6B41" }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.47 14.38c-.3-.15-1.73-.85-2-.95-.26-.1-.46-.15-.65.15-.2.3-.77.96-.94 1.16-.18.2-.35.22-.65.07-.3-.15-1.24-.46-2.36-1.46-.87-.78-1.46-1.73-1.63-2.03-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.18.2-.3.3-.5.1-.2.05-.36-.02-.51-.07-.15-.66-1.6-.9-2.18-.24-.58-.49-.5-.67-.51a5.2 5.2 0 0 0-.57-.01c-.2 0-.51.07-.78.37-.27.3-1.03 1.01-1.03 2.46 0 1.45 1.05 2.85 1.2 3.05.15.2 2.04 3.28 5.02 4.45 2.47.97 2.97.78 3.5.73.53-.05 1.71-.67 1.95-1.35.24-.68.24-1.29.17-1.42-.07-.12-.26-.19-.55-.34zM12.04 21.8h-.01a9.9 9.9 0 0 1-4.79-1.22L2 21.96l1.45-5.09a9.86 9.86 0 0 1-1.32-4.96C2.13 6.45 6.58 2 12.04 2s9.91 4.45 9.91 9.91-4.44 9.89-9.91 9.89z"/></svg>
          </a>
        </>
      )}
    </Box>
  );
};

const ReminderCard = ({ reminder }) => (
  <Card
    variant="outlined"
    sx={{
      mb: 0,
      border: 0,
      borderRadius: 0,
      borderBottom: `1px solid ${LINE_SOFT}`,
      boxShadow: "none",
    }}
  >
    <CardContent sx={{ p: "11px 16px !important", display: "flex", gap: 1.25, alignItems: "flex-start" }}>
      <Box
        component="span"
        sx={{ flex: "0 0 7px", width: 7, height: 7, mt: "7px", borderRadius: "50%", background: "#E8A33D" }}
      />
      <Typography variant="body2" sx={{ fontFamily: FONT, fontSize: 13.5, lineHeight: 1.5, color: INK }}>
        {buildReminderMessage(reminder)}
      </Typography>
    </CardContent>
  </Card>
);

const ProjectCard = ({ project, onClick, pending }) => (
  <Card
    variant="outlined"
    sx={{
      mb: 0,
      cursor: "pointer",
      border: 0,
      borderRadius: 0,
      borderBottom: `1px solid ${LINE_SOFT}`,
      boxShadow: "none",
      "&:hover": {
        backgroundColor: "#F4F7FA",
      },
    }}
    onClick={onClick}
  >
    <CardContent sx={{ p: "12px 16px !important", display: "flex", alignItems: "center", gap: 1.25 }}>
      <Box component="span" sx={{ flex: "0 0 7px", width: 7, height: 7, borderRadius: "50%", background: "#E0A21B" }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography fontWeight={600} sx={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, color: INK, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {project.projectName}
        </Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 12, color: MUTED }}>Priority not set</Typography>
      </Box>
      {pending ? (
        <Box component="span" sx={{ flexShrink: 0, fontFamily: MONO, fontSize: 13, fontWeight: 600, color: "#8A5A08" }}>
          {pending}
        </Box>
      ) : null}
      <Box component="span" sx={{ flexShrink: 0, fontFamily: FONT, fontSize: 13, fontWeight: 600, color: PRIMARY }}>
        Set priority →
      </Box>
    </CardContent>
  </Card>
);